import React, { useEffect, useRef, useState } from "react";

/**
 * CAD-style Front/Back viewer with:
 *  - SVG wireframes per clothing type (front + back views)
 *  - Logo overlay rendered at specified placement
 *  - Auto-rotate toggle (flips between front ⇄ back every 3s)
 *  - Manual front/back toggle buttons
 *  - Drag horizontally to flip view
 */

const FRONT = {
  hoodie: (
    <g>
      <path d="M 130 55 Q 160 30 190 55 L 195 80 Q 160 60 125 80 Z" />
      <path d="M 90 100 L 100 240 L 220 240 L 230 100 L 200 75 L 120 75 Z" />
      <path d="M 90 100 L 55 180 L 80 195 L 110 110 Z" />
      <path d="M 230 100 L 265 180 L 240 195 L 210 110 Z" />
      <path d="M 130 160 L 125 210 L 195 210 L 190 160 Z" />
      <path d="M 140 75 Q 160 90 180 75" />
      <line x1="160" y1="90" x2="160" y2="240" strokeDasharray="3 3" />
    </g>
  ),
  "t-shirt": (
    <g>
      <path d="M 135 60 Q 160 78 185 60" />
      <path d="M 105 90 L 115 230 L 205 230 L 215 90 L 185 65 L 160 78 L 135 65 Z" />
      <path d="M 105 90 L 80 130 L 100 140 L 125 100 Z" />
      <path d="M 215 90 L 240 130 L 220 140 L 195 100 Z" />
    </g>
  ),
  sweatshirt: (
    <g>
      <path d="M 135 60 Q 160 78 185 60 L 183 72 Q 160 88 137 72 Z" />
      <path d="M 95 95 L 105 235 L 215 235 L 225 95 L 195 72 L 125 72 Z" />
      <path d="M 95 95 L 65 185 L 90 195 L 115 105 Z" />
      <path d="M 225 95 L 255 185 L 230 195 L 205 105 Z" />
      <rect x="105" y="225" width="110" height="10" />
    </g>
  ),
  jumper: (
    <g>
      <path d="M 135 62 Q 160 78 185 62" />
      <path d="M 100 90 L 110 235 L 210 235 L 220 90 L 190 70 L 130 70 Z" />
      <path d="M 100 90 L 70 190 L 90 200 L 120 105 Z" />
      <path d="M 220 90 L 250 190 L 230 200 L 200 105 Z" />
    </g>
  ),
  jacket: (
    <g>
      <path d="M 125 60 L 140 85 L 160 75 L 180 85 L 195 60 L 190 80 L 160 95 L 130 80 Z" />
      <path d="M 95 90 L 105 245 L 215 245 L 225 90 L 195 70 L 125 70 Z" />
      <path d="M 95 90 L 65 195 L 90 205 L 115 100 Z" />
      <path d="M 225 90 L 255 195 L 230 205 L 205 100 Z" />
      <line x1="160" y1="85" x2="160" y2="240" strokeDasharray="2 2" />
      <rect x="115" y="170" width="35" height="25" />
      <rect x="170" y="170" width="35" height="25" />
    </g>
  ),
  shirt: (
    <g>
      <path d="M 130 55 L 145 80 L 160 75 L 175 80 L 190 55" />
      <path d="M 105 90 L 115 240 L 205 240 L 215 90 L 185 70 L 135 70 Z" />
      <path d="M 105 90 L 75 200 L 100 210 L 125 100 Z" />
      <path d="M 215 90 L 245 200 L 220 210 L 195 100 Z" />
      <line x1="160" y1="80" x2="160" y2="240" />
      {[100, 125, 150, 175, 200, 225].map((y) => (<circle key={y} cx="160" cy={y} r="2" />))}
    </g>
  ),
  pants: (
    <g>
      <rect x="105" y="40" width="110" height="18" />
      <path d="M 105 58 L 120 260 L 155 260 L 160 130 L 165 260 L 200 260 L 215 58 Z" />
      <line x1="160" y1="58" x2="160" y2="130" strokeDasharray="3 3" />
      <path d="M 115 70 L 135 90" />
      <path d="M 205 70 L 185 90" />
    </g>
  ),
  jeans: (
    <g>
      <rect x="105" y="40" width="110" height="20" />
      <path d="M 105 60 L 120 260 L 155 260 L 160 135 L 165 260 L 200 260 L 215 60 Z" />
      <line x1="160" y1="60" x2="160" y2="135" strokeDasharray="4 2" />
      <path d="M 112 70 L 132 100 L 140 70" />
      <path d="M 208 70 L 188 100 L 180 70" />
      <circle cx="114" cy="98" r="1.5" />
      <circle cx="206" cy="98" r="1.5" />
    </g>
  ),
  shorts: (
    <g>
      <rect x="105" y="55" width="110" height="18" />
      <path d="M 105 73 L 115 180 L 155 180 L 160 120 L 165 180 L 205 180 L 215 73 Z" />
      <line x1="160" y1="73" x2="160" y2="120" strokeDasharray="3 3" />
    </g>
  ),
  cap: (
    <g>
      <path d="M 90 140 Q 160 50 230 140 Z" />
      <ellipse cx="160" cy="140" rx="90" ry="18" />
      <line x1="160" y1="55" x2="160" y2="140" />
      <circle cx="160" cy="62" r="4" />
    </g>
  ),
  dress: (
    <g>
      <path d="M 135 55 Q 160 75 185 55" />
      <path d="M 115 80 L 85 275 L 235 275 L 205 80 L 185 65 L 160 75 L 135 65 Z" />
      <path d="M 115 80 L 95 115 L 115 120 L 130 90 Z" />
      <path d="M 205 80 L 225 115 L 205 120 L 190 90 Z" />
      <line x1="130" y1="160" x2="190" y2="160" strokeDasharray="3 3" />
    </g>
  ),
};

// Back views: no pocket/zipper/placket details — often just body shape + seam lines + hood back
const BACK = {
  hoodie: (
    <g>
      <path d="M 125 55 Q 160 32 195 55 L 200 95 Q 160 75 120 95 Z" />
      <path d="M 90 100 L 100 240 L 220 240 L 230 100 L 200 75 L 120 75 Z" />
      <path d="M 90 100 L 55 180 L 80 195 L 110 110 Z" />
      <path d="M 230 100 L 265 180 L 240 195 L 210 110 Z" />
      <line x1="160" y1="95" x2="160" y2="240" strokeDasharray="3 3" />
      <path d="M 100 100 Q 160 105 220 100" />
    </g>
  ),
  "t-shirt": (
    <g>
      <path d="M 135 65 Q 160 72 185 65" />
      <path d="M 105 90 L 115 230 L 205 230 L 215 90 L 185 70 L 135 70 Z" />
      <path d="M 105 90 L 80 130 L 100 140 L 125 100 Z" />
      <path d="M 215 90 L 240 130 L 220 140 L 195 100 Z" />
      <line x1="105" y1="90" x2="215" y2="90" strokeDasharray="2 3" />
    </g>
  ),
  sweatshirt: (
    <g>
      <path d="M 135 65 Q 160 72 185 65 L 183 75 Q 160 82 137 75 Z" />
      <path d="M 95 95 L 105 235 L 215 235 L 225 95 L 195 72 L 125 72 Z" />
      <path d="M 95 95 L 65 185 L 90 195 L 115 105 Z" />
      <path d="M 225 95 L 255 185 L 230 195 L 205 105 Z" />
      <rect x="105" y="225" width="110" height="10" />
    </g>
  ),
  jumper: (
    <g>
      <path d="M 135 70 Q 160 78 185 70" />
      <path d="M 100 90 L 110 235 L 210 235 L 220 90 L 190 70 L 130 70 Z" />
      <path d="M 100 90 L 70 190 L 90 200 L 120 105 Z" />
      <path d="M 220 90 L 250 190 L 230 200 L 200 105 Z" />
    </g>
  ),
  jacket: (
    <g>
      <path d="M 125 60 L 140 85 L 180 85 L 195 60" />
      <path d="M 95 90 L 105 245 L 215 245 L 225 90 L 195 70 L 125 70 Z" />
      <path d="M 95 90 L 65 195 L 90 205 L 115 100 Z" />
      <path d="M 225 90 L 255 195 L 230 205 L 205 100 Z" />
      <path d="M 100 120 Q 160 130 220 120" strokeDasharray="2 3" />
    </g>
  ),
  shirt: (
    <g>
      <path d="M 130 60 L 160 75 L 190 60" />
      <path d="M 105 90 L 115 240 L 205 240 L 215 90 L 185 70 L 135 70 Z" />
      <path d="M 105 90 L 75 200 L 100 210 L 125 100 Z" />
      <path d="M 215 90 L 245 200 L 220 210 L 195 100 Z" />
      <path d="M 105 100 Q 160 108 215 100" strokeDasharray="2 3" />
    </g>
  ),
  pants: (
    <g>
      <rect x="105" y="40" width="110" height="18" />
      <path d="M 105 58 L 120 260 L 155 260 L 160 130 L 165 260 L 200 260 L 215 58 Z" />
      <rect x="120" y="70" width="30" height="30" />
      <rect x="170" y="70" width="30" height="30" />
    </g>
  ),
  jeans: (
    <g>
      <rect x="105" y="40" width="110" height="20" />
      <path d="M 105 60 L 120 260 L 155 260 L 160 135 L 165 260 L 200 260 L 215 60 Z" />
      <rect x="118" y="72" width="34" height="32" />
      <rect x="168" y="72" width="34" height="32" />
    </g>
  ),
  shorts: (
    <g>
      <rect x="105" y="55" width="110" height="18" />
      <path d="M 105 73 L 115 180 L 155 180 L 160 120 L 165 180 L 205 180 L 215 73 Z" />
      <rect x="125" y="85" width="25" height="22" />
      <rect x="170" y="85" width="25" height="22" />
    </g>
  ),
  cap: (
    <g>
      <path d="M 90 140 Q 160 60 230 140 Z" />
      <path d="M 120 140 L 120 165 L 200 165 L 200 140" />
      <rect x="145" y="150" width="30" height="10" />
    </g>
  ),
  dress: (
    <g>
      <path d="M 135 60 Q 160 75 185 60" />
      <path d="M 115 80 L 85 275 L 235 275 L 205 80 L 185 65 L 160 75 L 135 65 Z" />
      <path d="M 115 80 L 95 115 L 115 120 L 130 90 Z" />
      <path d="M 205 80 L 225 115 L 205 120 L 190 90 Z" />
      <line x1="160" y1="80" x2="160" y2="270" strokeDasharray="3 3" />
    </g>
  ),
};

const getShapeKey = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("hood")) return "hoodie";
  if (t.includes("tee") || t.includes("t-shirt")) return "t-shirt";
  if (t.includes("sweat")) return "sweatshirt";
  if (t.includes("jump")) return "jumper";
  if (t.includes("jack") || t.includes("coat")) return "jacket";
  if (t.includes("shirt")) return "shirt";
  if (t.includes("jean")) return "jeans";
  if (t.includes("pant") || t.includes("jogger") || t.includes("trous")) return "pants";
  if (t.includes("short")) return "shorts";
  if (t.includes("cap") || t.includes("hat") || t.includes("beanie")) return "cap";
  if (t.includes("dress")) return "dress";
  return "t-shirt";
};

/**
 * Logo placement coordinates in the 320x320 SVG viewBox for each garment shape.
 * Returns {x, y, w, h} based on logo size (0.1–1.0) and placement slot.
 */
const LOGO_SLOTS = {
  // Format: [cx, cy] where logo is centered, base size 60px at size=1.0
  "front-chest-left":   { cx: 130, cy: 120, base: 35 },
  "front-chest-center": { cx: 160, cy: 125, base: 45 },
  "front-chest-right":  { cx: 190, cy: 120, base: 35 },
  "front-center-large": { cx: 160, cy: 170, base: 80 },
  "back-top-center":    { cx: 160, cy: 110, base: 50 },
  "back-center-large":  { cx: 160, cy: 170, base: 90 },
  "sleeve-left":        { cx: 80,  cy: 145, base: 22 },
  "sleeve-right":       { cx: 240, cy: 145, base: 22 },
  "none":               null,
};

const ClothingViewer3D = ({
  type = "hoodie",
  color = "#d4af37",
  logoOverlay = null, // { image_base64, placement, size }
  autoRotate = false,
  imageSrc = null,    // Front view image (AI-generated or uploaded)
  imageBack = null,   // Back view image (optional)
  imageSide = null,   // Side view image (optional)
}) => {
  const shapeKey = getShapeKey(type);
  const [view, setView] = useState("front"); // "front" | "back" | "side"
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startView = useRef("front");

  // Which image to show based on current view
  const activeImage =
    view === "back"  ? (imageBack || imageSrc) :
    view === "side"  ? (imageSide || imageSrc) :
                       imageSrc;

  const hasMultipleViews = !!(imageBack || imageSide);

  // Auto-rotate between available views
  useEffect(() => {
    if (!autoRotate) return;
    const views = ["front"];
    if (imageBack) views.push("back");
    if (imageSide) views.push("side");
    if (views.length < 2) return;  // no point rotating one view

    let idx = 0;
    const id = setInterval(() => {
      idx = (idx + 1) % views.length;
      setView(views[idx]);
    }, 3500);
    return () => clearInterval(id);
  }, [autoRotate, imageBack, imageSide]);

  const shape = (view === "front" ? FRONT : BACK)[shapeKey] || FRONT["t-shirt"];

  // --- Logo overlay as absolute-positioned DIV for IMAGE mode ---
  // CSS percentages for each placement slot (matches SVG coords roughly)
  const CSS_SLOTS = {
    "front-chest-left":   { top: "30%",  left: "38%", size: 0.18 },
    "front-chest-center": { top: "32%",  left: "50%", size: 0.22 },
    "front-chest-right":  { top: "30%",  left: "62%", size: 0.18 },
    "front-center-large": { top: "50%",  left: "50%", size: 0.35 },
    "back-top-center":    { top: "28%",  left: "50%", size: 0.22 },
    "back-center-large":  { top: "50%",  left: "50%", size: 0.38 },
    "sleeve-left":        { top: "40%",  left: "22%", size: 0.12 },
    "sleeve-right":       { top: "40%",  left: "78%", size: 0.12 },
  };

  let cssLogo = null;
  if (imageSrc && logoOverlay && logoOverlay.image_base64 && logoOverlay.placement && logoOverlay.placement !== "none") {
    const place = logoOverlay.placement;
    const onFront = !place.startsWith("back");
    if ((view === "front" && onFront) || (view === "back" && !onFront)) {
      const slot = CSS_SLOTS[place];
      if (slot) {
        const scale = Math.max(0.2, Math.min(1.5, logoOverlay.size ?? 1));
        const size = slot.size * scale;
        const src = logoOverlay.image_base64.startsWith("data:")
          ? logoOverlay.image_base64
          : `data:image/png;base64,${logoOverlay.image_base64}`;
        cssLogo = (
          <div
            className="absolute pointer-events-none"
            style={{
              top: slot.top,
              left: slot.left,
              width: `${size * 100}%`,
              transform: "translate(-50%, -50%)",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))",
            }}
          >
            <img src={src} alt="logo" className="w-full h-auto object-contain" />
          </div>
        );
      }
    }
  }

  // Compute SVG logo slot (for wireframe mode)
  let logoNode = null;
  if (!imageSrc && logoOverlay && logoOverlay.image_base64 && logoOverlay.placement && logoOverlay.placement !== "none") {
    const place = logoOverlay.placement;
    const onFront = !place.startsWith("back");
    if ((view === "front" && onFront) || (view === "back" && !onFront)) {
      const slot = LOGO_SLOTS[place];
      if (slot) {
        const size = Math.max(0.2, Math.min(1.5, logoOverlay.size ?? 1));
        const w = slot.base * size;
        const h = slot.base * size;
        const src = logoOverlay.image_base64.startsWith("data:")
          ? logoOverlay.image_base64
          : `data:image/png;base64,${logoOverlay.image_base64}`;
        logoNode = (
          <image
            href={src}
            x={slot.cx - w / 2}
            y={slot.cy - h / 2}
            width={w}
            height={h}
            preserveAspectRatio="xMidYMid meet"
          />
        );
      }
    }
  }

  // Drag-to-rotate: each 60px of horizontal drag steps through Front → Side → Back → Side → Front.
  // Uses a ref-tracked sequence so state updates don't get stuck mid-gesture.
  const viewSeq = React.useMemo(() => {
    // Ordered carousel of views (wraps circularly)
    const seq = ["front"];
    if (imageSide) seq.push("side");
    if (imageBack) seq.push("back");
    if (imageSide) seq.push("side"); // side again on the way back
    return seq.length > 1 ? seq : ["front"];
  }, [imageSide, imageBack]);
  const seqIndex = useRef(0);
  const lastStepX = useRef(0);

  const setViewByIndex = (idx) => {
    if (!viewSeq.length) return;
    const wrapped = ((idx % viewSeq.length) + viewSeq.length) % viewSeq.length;
    seqIndex.current = wrapped;
    setView(viewSeq[wrapped]);
  };

  const onMouseDown = (e) => {
    if (viewSeq.length <= 1) return; // nothing to rotate
    setDragging(true);
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    startX.current = x;
    lastStepX.current = x;
    startView.current = view;
    // Lock the starting index to whatever the current view is
    const i = viewSeq.indexOf(view);
    seqIndex.current = i >= 0 ? i : 0;
  };
  const onMouseMove = (e) => {
    if (!dragging || viewSeq.length <= 1) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const stepPx = 55; // finger travel per view-step
    const delta = x - lastStepX.current;
    if (Math.abs(delta) >= stepPx) {
      // Dragging LEFT (negative delta) rotates the garment clockwise (front → side → back)
      const dir = delta < 0 ? 1 : -1;
      setViewByIndex(seqIndex.current + dir);
      lastStepX.current = x;
    }
  };
  const onMouseUp = () => setDragging(false);

  return (
    <div
      className="relative w-full h-full bg-[#0a0a0a] border border-zinc-800 overflow-hidden select-none"
      data-testid="viewer-3d"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onMouseDown}
      onTouchMove={onMouseMove}
      onTouchEnd={onMouseUp}
      style={{ cursor: dragging ? "grabbing" : (viewSeq.length > 1 ? "grab" : "default"), touchAction: "pan-y" }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#1a1a1a 1px, transparent 1px), linear-gradient(90deg, #1a1a1a 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      <div className="absolute top-0 left-0 w-5 h-5 border-l-2 border-t-2 border-[#d4af37] z-10 pointer-events-none" />
      <div className="absolute top-0 right-0 w-5 h-5 border-r-2 border-t-2 border-[#d4af37] z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-l-2 border-b-2 border-[#d4af37] z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-r-2 border-b-2 border-[#d4af37] z-10 pointer-events-none" />

      {/* HUD */}
      <div className="absolute top-3 left-3 z-10 text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono pointer-events-none">
        // CAD.{view.toUpperCase()}_VIEW
      </div>
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase text-[#d4af37] font-mono pointer-events-none">
        <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full pulse-red inline-block" />
        LIVE
      </div>
      <div className="absolute bottom-3 left-3 z-10 text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono pointer-events-none">
        {viewSeq.length > 1 ? "SWIPE TO ROTATE" : shapeKey.toUpperCase()}
      </div>

      {/* Front/Back/Side toggle */}
      <div className="absolute bottom-3 right-3 z-20 flex gap-1">
        <button
          data-testid="view-front"
          onClick={() => setView("front")}
          className={`px-3 py-1 text-[9px] tracking-[0.2em] uppercase border transition-colors ${
            view === "front" ? "bg-[#d4af37] border-[#d4af37] text-white" : "border-zinc-700 text-zinc-500 hover:text-white"
          }`}
        >
          FRONT
        </button>
        <button
          data-testid="view-back"
          onClick={() => setView("back")}
          disabled={!imageSrc ? false : !imageBack}
          className={`px-3 py-1 text-[9px] tracking-[0.2em] uppercase border transition-colors ${
            view === "back" ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-zinc-700 text-zinc-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
          }`}
        >
          BACK
        </button>
        {imageSide && (
          <button
            data-testid="view-side"
            onClick={() => setView("side")}
            className={`px-3 py-1 text-[9px] tracking-[0.2em] uppercase border transition-colors ${
              view === "side" ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-zinc-700 text-zinc-500 hover:text-white"
            }`}
          >
            SIDE
          </button>
        )}
      </div>

      {/* Rotating ring */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[85%] h-[85%] rounded-full border border-dashed border-zinc-800"
          style={{ animation: "spin 40s linear infinite" }}
        />
      </div>

      {/* Scanning line */}
      <div
        className="absolute left-0 right-0 h-[2px] pointer-events-none z-[5]"
        style={{
          background: "linear-gradient(90deg, transparent, #d4af37, transparent)",
          animation: "scanV 4s linear infinite",
        }}
      />

      {/* SVG wireframe with view flip animation */}
      <div
        key={view}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          animation: imageSrc
            ? "flipIn 0.6s ease-out"
            : "flipIn 0.6s ease-out, float 6s ease-in-out 0.6s infinite",
        }}
      >
        {imageSrc ? (
          // === IMAGE MODE — show actual product photo with 3D rotation + logo overlay ===
          <div
            className="relative w-full h-full p-4 sm:p-6 flex items-center justify-center"
            style={{
              animation: autoRotate && hasMultipleViews ? "rotate3d 10s ease-in-out infinite" : "none",
              transformStyle: "preserve-3d",
            }}
          >
            <img
              src={activeImage}
              alt=""
              className="max-w-full max-h-full object-contain"
              style={{
                filter: "drop-shadow(0 20px 30px rgba(255,51,51,0.15)) drop-shadow(0 0 15px rgba(0,0,0,0.5))",
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            {cssLogo}
            {!hasMultipleViews && (
              <div className="absolute bottom-14 left-1/2 -translate-x-1/2 text-[8px] tracking-[0.25em] uppercase text-zinc-500 font-mono bg-black/60 px-2 py-1 border border-zinc-800">
                // UPLOAD BACK/SIDE PHOTOS FOR TRUE 360°
              </div>
            )}
          </div>
        ) : (
          <svg
            viewBox="0 0 320 320"
            width="72%"
            height="72%"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 12px ${color}50)` }}
          >
            {/* Fabric fill — subtle */}
            <g fill={color} opacity="0.08" stroke="none">
              {shape}
            </g>
            {/* Wireframe stroke */}
            <g>{shape}</g>
            {/* Logo overlay (on top of garment) */}
            {logoNode && <g>{logoNode}</g>}
          </svg>
        )}
      </div>

      <style>{`
        @keyframes scanV {
          0% { top: 0; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-0.5deg); }
          50% { transform: translateY(-6px) rotate(0.5deg); }
        }
        @keyframes flipIn {
          0% { transform: perspective(800px) rotateY(-90deg) scale(0.9); opacity: 0; }
          100% { transform: perspective(800px) rotateY(0deg) scale(1); opacity: 1; }
        }
        @keyframes rotate3d {
          0%   { transform: perspective(900px) rotateY(-14deg) rotateX(1deg); }
          50%  { transform: perspective(900px) rotateY(14deg) rotateX(-1deg); }
          100% { transform: perspective(900px) rotateY(-14deg) rotateX(1deg); }
        }
      `}</style>
    </div>
  );
};

export default ClothingViewer3D;
